import { apiService } from './api';
import type { AuditEventsPage, AuditEventFacets, AuditEventFilters } from '@/types';

/**
 * Turn the filter object into query params (CSV-joins array fields).
 * The actor is sent as the single `actor` key ('u:<id>' | 'e:<email>' | 'system' | 'anon');
 * the legacy actorUserId/actorEmail params are no longer sent.
 */
export function toParams(filters: AuditEventFilters): Record<string, string> {
  const p: Record<string, string> = {};
  if (filters.from) p.from = filters.from;
  if (filters.to) p.to = filters.to;
  if (filters.category?.length) p.category = filters.category.join(',');
  if (filters.eventType?.length) p.eventType = filters.eventType.join(',');
  if (filters.status?.length) p.status = filters.status.join(',');
  if (filters.actor) p.actor = filters.actor;
  if (filters.clientId) p.clientId = filters.clientId;
  if (filters.entityType) p.entityType = filters.entityType;
  if (filters.entityId) p.entityId = filters.entityId;
  if (filters.ip) p.ip = filters.ip;
  if (filters.q) p.q = filters.q;
  return p;
}

class AuditEventsService {
  async getEvents(filters: AuditEventFilters, cursor?: string | null, limit = 50): Promise<AuditEventsPage> {
    const params: Record<string, string | number> = { ...toParams(filters), limit };
    if (cursor) params.cursor = cursor;
    // Uses /api/activity/* (not /audit/*) so ad/privacy blockers don't drop the XHR.
    const res = await apiService.client.get<{ success: boolean; data: AuditEventsPage['data']; nextCursor: string | null }>(
      '/activity/events',
      { params }
    );
    return { data: res.data.data ?? [], nextCursor: res.data.nextCursor ?? null };
  }

  async getFacets(filters: AuditEventFilters): Promise<AuditEventFacets> {
    const res = await apiService.client.get<{ success: boolean; data: AuditEventFacets }>('/activity/events/facets', {
      params: toParams(filters),
    });
    return res.data.data;
  }

  /**
   * CSV export. `tz` is the browser's IANA zone so the server can add a local-time column.
   * If the server's row cap is hit, the LAST line starts with `"TRUNCATED` (see csvIsTruncated).
   */
  async exportCsv(filters: AuditEventFilters, tz?: string): Promise<Blob> {
    const params: Record<string, string> = toParams(filters);
    if (tz) params.tz = tz;
    const res = await apiService.client.get<Blob>('/activity/events/export.csv', {
      params,
      responseType: 'blob',
    });
    return res.data;
  }
}

export const auditEventsService = new AuditEventsService();
export default auditEventsService;
